import type { KyInstance } from 'ky'
import type { PaymentPlatform, ServiceApiPlatform } from './types'

export interface ExperimentProduct {
  id: number // product_id for purchase API
  title: string
  iap_sku: string
  iap_sku_ios: string
  iap_sku_android: string
  iap_sku_android_cn: string
}

export interface FetchExperimentProductsOptions {
  userId: number
  platform: ServiceApiPlatform
  storeRegion?: string | null
}

/**
 * Fetches products for the pricing experiment from the Service API.
 *
 * @param serviceApi - The ky instance for the service API
 * @param options - Options including userId, platform, and optional storeRegion
 * @returns Array of experiment products
 */
export async function fetchExperimentProducts(
  serviceApi: KyInstance,
  options: FetchExperimentProductsOptions,
): Promise<ExperimentProduct[]> {
  const { userId, platform, storeRegion } = options

  const searchParams: Record<string, string> = {
    platform,
    product_type: 'subscription',
  }

  if (storeRegion) {
    searchParams.store_region = storeRegion
  }

  const data = await serviceApi
    .get(`store/users/${userId}/products`, { searchParams })
    .json<ExperimentProduct[]>()

  return data
}

export interface InferredProductType {
  isAnnual: boolean
  isEarlyBird: boolean
}

/**
 * Infers product type (annual/monthly, early bird/default) from the product title.
 *
 * @param title - The product title from the API
 * @returns Object with isAnnual and isEarlyBird flags
 */
export function inferProductType(title: string): InferredProductType {
  const lowerTitle = title.toLowerCase()

  const isAnnual =
    lowerTitle.includes('年方案') ||
    lowerTitle.includes('annual') ||
    lowerTitle.includes('yearly') ||
    lowerTitle.includes('year')

  const isEarlyBird =
    lowerTitle.includes('早鳥') ||
    lowerTitle.includes('early bird') ||
    lowerTitle.includes('earlybird') ||
    lowerTitle.includes('early_bird')

  return { isAnnual, isEarlyBird }
}

/**
 * Gets the platform-specific SKU from an experiment product.
 *
 * @param product - The experiment product
 * @param platform - The payment platform
 * @returns The appropriate SKU for the given platform
 */
export function getSkuForPlatform(
  product: ExperimentProduct,
  platform: PaymentPlatform,
): string {
  switch (platform) {
    case 'ios':
      return product.iap_sku_ios
    case 'googleplay_android':
      return product.iap_sku_android
    case 'china_android':
      return product.iap_sku_android_cn
  }
}
